


import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, addDoc, doc as firestoreDoc, updateDoc, getDoc, Timestamp } from 'firebase/firestore';
import { auth, db } from '../firebase'; // Adjust the import path as needed
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';

const BrandingDesigns = () => {
  const [brandingDesigns, setBrandingDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);
  
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredDesigns, setFilteredDesigns] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [orderLoading, setOrderLoading] = useState(false);
  const [orderQuantities, setOrderQuantities] = useState({});

  useEffect(() => {
    const fetchBrandingDesigns = async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          throw new Error("User not logged in");
        }

        setLoading(true);
        const brandingRef = collection(db, 'users', user.uid, 'BrandingDesigns');
        const q = query(brandingRef, orderBy('timestamp', 'desc'));
        const querySnapshot = await getDocs(q);
        
        const designs = [];
        querySnapshot.forEach((doc) => {
          designs.push({
            id: doc.id,
            ...doc.data()
          });
        });
        
        setBrandingDesigns(designs);
        // Add this after setBrandingDesigns(designs); and before setError(null);
if (designs.length > 0) {
  // Fetch order quantities after designs are loaded
  setTimeout(() => fetchOrderQuantities(), 100);
}
        setFilteredDesigns(designs);
        setError(null);
      } catch (err) {
        console.error('Error fetching branding designs:', err);
        setError('Failed to load branding designs');
      } finally {
        setLoading(false);
      }
    };

    const fetchWalletBalance = async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;

        const walletRef = firestoreDoc(db, 'users', user.uid, 'wallet', 'walletDetails');
        const walletDoc = await getDoc(walletRef);
        
        if (walletDoc.exists()) {
          setWalletBalance(walletDoc.data().balance || 0);
        }
      } catch (err) {
        console.error('Error fetching wallet balance:', err);
      }
    };

    fetchBrandingDesigns();
    fetchWalletBalance();
  }, []);

  // Filter designs based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredDesigns(brandingDesigns);
    } else {
      const filtered = brandingDesigns.filter(design => 
        (design.productName?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (design.designName?.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredDesigns(filtered);
    }
  }, [searchTerm, brandingDesigns]);

  const openModal = () => {
    setIsModalOpen(true);
    setSelectedItems([]);
    setSearchTerm('');
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedItems([]);
    setSearchTerm('');
  };

  const openConfirmModal = () => {
    if (selectedItems.length === 0) {
      alert('Please select at least one item to place an order.');
      return;
    }

    const totalCost = calculateTotal();
    if (walletBalance < totalCost) {
      alert(`Insufficient wallet balance. Required: ₹${totalCost}, Available: ₹${walletBalance}`);
      return;
    }

    setIsConfirmModalOpen(true);
  };

  const closeConfirmModal = () => {
    setIsConfirmModalOpen(false);
  };

  const addToOrder = (design) => {
    const existingItem = selectedItems.find(item => item.id === design.id);
    if (existingItem) {
      setSelectedItems(prev => 
        prev.map(item => 
          item.id === design.id 
            ? { ...item, quantity: item.quantity + 1 }
            : item
        )
      );
    } else {
      setSelectedItems(prev => [...prev, { 
        ...design, 
        quantity: 1,
        unitPrice: design.totalPrice || 0
      }]);
    }
  };

  const updateQuantity = (designId, newQuantity) => {
    if (newQuantity <= 0) {
      setSelectedItems(prev => prev.filter(item => item.id !== designId));
    } else {
      setSelectedItems(prev => 
        prev.map(item => 
          item.id === designId 
            ? { ...item, quantity: newQuantity }
            : item
        )
      );
    }
  };



  const getOrderQuantityForDesign = async (userId, designSKU) => {
    try {
      if (!designSKU) return 0;

      // Fetch from BrandingOrders collection
      const brandingOrdersRef = collection(db, 'users', userId, 'BrandingOrders');
      const brandingOrdersSnapshot = await getDocs(brandingOrdersRef);
      
      let totalOrderQuantity = 0;

      brandingOrdersSnapshot.forEach((doc) => {
        const orderData = doc.data();
        
        // Check if order is not delivered (assuming status field exists)
        const isNotDelivered = !orderData.status || orderData.status.toLowerCase() !== 'delivered';
        
        if (isNotDelivered && orderData.order_items) {
          orderData.order_items.forEach((item) => {
            if (item.sku === designSKU) {
              totalOrderQuantity += item.units || 0;
            }
          });
        }
      });

      return totalOrderQuantity;
    } catch (error) {
      console.error('Error fetching order quantity for design:', error);
      return 0;
    }
  };

  const fetchOrderQuantities = async () => {
  try {
    const user = auth.currentUser;
    if (!user) return;

    const quantities = {};
    for (const design of brandingDesigns) {
      if (design.SKU) {
        quantities[design.id] = await getOrderQuantityForDesign(user.uid, design.SKU);
      }
    }
    setOrderQuantities(quantities);
  } catch (error) {
    console.error('Error fetching order quantities:', error);
  }
};



  const calculateTotal = () => {
    return selectedItems.reduce((total, item) => {
      return total + ((item.unitPrice || 0) * item.quantity);
    }, 0);
  };

  const confirmOrder = async () => {
    setOrderLoading(true);
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error("User not logged in");
      }

      const totalCost = calculateTotal();
      const confirmBalance = walletBalance - totalCost;

      // Update wallet balance
      const walletRef = firestoreDoc(db, 'users', user.uid, 'wallet', 'walletDetails');
      await updateDoc(walletRef, { balance: confirmBalance });
      
      // Record transaction
      const transactionsRef = collection(walletRef, 'transactions');
      const transactionData = {
        type: 'branding',
        amount: totalCost,
        balanceBefore: walletBalance,
        balanceAfter: confirmBalance,
        timestamp: Timestamp.now(),
      };

      await addDoc(transactionsRef, transactionData);

      // Prepare order data for API
      const orderData = {
        userId: user.uid,
        items: selectedItems.map(item => ({
          designId: item.id,
          productName: item.productName,
          designName: item.designName,
          SKU: item.SKU,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          totalPrice: item.unitPrice * item.quantity,
          image: item.images?.front || null
        })),
        totalAmount: totalCost
      };

      // Call the branding order API
      const response = await fetch('http://localhost:8080/create-branding-shiprocket-order', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(orderData),
      });

      const result = await response.json();

      if (response.ok) {
        alert(`Branding order placed successfully! Order ID: ${result.orderId}`);
        setWalletBalance(confirmBalance); // Update local wallet balance
        closeConfirmModal();
        closeModal();
      } else {
        // If API call fails, revert wallet transaction
        await updateDoc(walletRef, { balance: walletBalance });
        // Remove the transaction record
        // Note: You might want to implement a more sophisticated rollback mechanism
        throw new Error(result.error || 'Failed to create branding order');
      }

    } catch (err) {
      console.error('Error placing branding order:', err);
      alert('Failed to place branding order. Please try again.');
    } finally {
      setOrderLoading(false);
    }
  };


  if (loading) {
    return (
      <div className="dashboard">
        <Header />
        <div className="dashboard-container">
          <Sidebar />
          <div className="main-content">
            <h1>Your Branding Designs</h1>
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard">
        <Header />
        <div className="dashboard-container">
          <Sidebar />
          <div className="main-content">
            <h1>Your Branding Designs</h1>
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <div style={{ color: 'red', marginBottom: '10px' }}>{error}</div>
              <button 
                onClick={() => window.location.reload()} 
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <Header />
      <div className="dashboard-container">
        <Sidebar />
        <div className="main-content">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
             <button
              onClick={openModal}
              style={{
                padding: '12px 24px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '500',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#218838'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#28a745'}
            >
              Create Order
            </button>
          </div>

          {brandingDesigns.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <h3>No branding designs yet</h3>
              <p>Create your first branding design to see it here.</p>
            </div>
          ) : (
            <div 
              className={`inventory-cards ${brandingDesigns.length <= 2 ? 'few-items' : ''}`}
              style={{
                display: 'grid',
                gap: '24px',
                padding: '20px 0',
                gridTemplateColumns: brandingDesigns.length === 1 
                  ? '1fr' 
                  : brandingDesigns.length === 2 
                    ? 'repeat(2, 1fr)' 
                    : 'repeat(auto-fit, minmax(280px, 1fr))',
                maxWidth: brandingDesigns.length <= 2 ? '600px' : '1200px',
                margin: '0 auto',
                justifyContent: brandingDesigns.length <= 2 ? 'center' : 'flex-start'
              }}
            >
              {brandingDesigns.map((design) => (
                <div 
                  key={design.id} 
                  className="inventory-card"
                  style={{
                    background: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    overflow: 'hidden',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    minWidth: brandingDesigns.length <= 2 ? '280px' : 'auto',
                    maxWidth: brandingDesigns.length <= 2 ? '320px' : 'auto'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-4px)';
                    e.target.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                  }}
                >
                  {/* Mockup Image */}
                  <div 
                    className="image-container"
                    style={{
                      width: '100%',
                      height: 'auto',
                      background: '#f8f9fa',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden'
                    }}
                  >
                    {design.images?.front ? (
                      <img 
                        src={design.images.front} 
                        alt={`${design.designName} mockup`}
                        className="image"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = '<p style="color: #666; font-size: 14px; margin: 0;">No Image</p>';
                        }}
                      />
                    ) : (
                      <p style={{color: '#666', fontSize: '14px', margin: '0'}}>No Image</p>
                    )}
                  </div>

                  {/* Card Content */}
                  <div style={{ padding: '16px' }}>
                    {/* Product Name */}
                    <h3>
                      {design.productName || 'Unknown Product'}
                    </h3>

                    {/* Design Name */}
                    <p>
                      Design: {design.designName || 'Untitled'}
                    </p>


                   <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      padding: '8px 0',
                      borderTop: '1px solid #eee'
                    }}>
                      <div style={{ textAlign: 'center', flex: 1 }}>
                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>
                          Ordered Qty
                        </div>
                        <div style={{ 
                          fontSize: '16px', 
                          fontWeight: 'bold', 
                          color: design.orderQuantity > 0 ? '#dc3545' : '#28a745' 
                        }}>
                         {orderQuantities[design.id] || 0}
                        </div>
                      </div>
                      
                      <div style={{ 
                        width: '1px', 
                        height: '30px', 
                        backgroundColor: '#eee' 
                      }}></div>
                      
                      <div style={{ textAlign: 'center', flex: 1 }}>
                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>
                          Usable Qty
                        </div>
                        <div style={{ 
                          fontSize: '16px', 
                          fontWeight: 'bold', 
                          color: design.usableQuantity > 0 ? '#28a745' : '#dc3545' 
                        }}>
                          {design.usableQuantity || 0}
                        </div>
                    </div>
                    </div>


                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Create Order Modal */}
          {isModalOpen && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}>
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                width: '90%',
                maxWidth: '800px',
                maxHeight: '90vh',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
              }}>
                {/* Modal Header */}
                <div style={{
                  padding: '20px',
                  borderBottom: '1px solid #eee',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <h2 style={{ margin: 0 }}>Create Order</h2>
                  <button
                    onClick={closeModal}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '24px',
                      cursor: 'pointer',
                      color: '#666'
                    }}
                  >
                    ×
                  </button>
                </div>

                {/* Search Bar */}
                <div style={{ padding: '20px', borderBottom: '1px solid #eee' }}>
                  <input
                    type="text"
                    placeholder="Search by product name or design name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '16px'
                    }}
                  />
                </div>

                {/* Modal Content */}
                <div style={{
                  flex: 1,
                  overflow: 'auto',
                  padding: '20px'
                }}>
                  <div style={{ marginBottom: '20px' }}>
                    {filteredDesigns.map((design) => (
                      <div key={design.id} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '12px',
                        marginBottom: '8px',
                        backgroundColor: 'white',
                        borderRadius: '6px',
                        border: '1px solid #ddd'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                          {/* Small Image */}
                          <div style={{
                            width: '50px',
                            height: '50px',
                            backgroundColor: '#f0f0f0',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                          }}>
                            {design.images?.front ? (
                              <img 
                                src={design.images.front} 
                                alt={design.designName}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  borderRadius: '4px'
                                }}
                              />
                            ) : (
                              <span style={{ color: '#666', fontSize: '10px' }}>No Image</span>
                            )}
                          </div>
                          
                          {/* Design Info */}
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '2px' }}>
                              {design.productName || 'Unknown Product'}
                            </div>
                            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                              {design.designName || 'Untitled'}
                            </div>
                            <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#28a745' }}>
                              ₹{design.totalPrice || 0}
                            </div>
                          </div>
                        </div>
                        
                        {/* Add to Order Button */}
                        <button
                          onClick={() => addToOrder(design)}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          Add to Order
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Selected Items */}
                  {selectedItems.length > 0 && (
                    <div style={{
                      marginTop: '20px',
                      padding: '20px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '8px'
                    }}>
                      <h3>Selected Items:</h3>
                      {selectedItems.map((item) => (
                        <div key={item.id} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '12px',
                          marginBottom: '8px',
                          backgroundColor: 'white',
                          borderRadius: '6px'
                        }}>
                          <div>
                            <strong>{item.productName}</strong> - {item.designName}
                            <br />
                            <span style={{ color: '#666' }}>₹{item.unitPrice} each</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <button
                              onClick={() => updateQuantity(item.id, item.quantity - 1)}
                              style={{
                                width: '30px',
                                height: '30px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '1px solid #ddd',
                                backgroundColor: '#28a745',
                                cursor: 'pointer'
                              }}
                            >
                              -
                            </button>
                            <span style={{ minWidth: '20px', textAlign: 'center' }}>
                              {item.quantity}
                            </span>
                            <button
                              onClick={() => updateQuantity(item.id, item.quantity + 1)}
                              style={{
                                width: '30px',
                                height: '30px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '1px solid #ddd',
                                backgroundColor: '#28a745',
                                cursor: 'pointer'
                              }}
                            >
                              +
                            </button>
                            <span style={{ marginLeft: '10px', fontWeight: 'bold' }}>
                              ₹{item.unitPrice * item.quantity}
                            </span>
                          </div>
                        </div>
                      ))}
                      
                      <div style={{
                        borderTop: '2px solid #28a745',
                        paddingTop: '12px',
                        textAlign: 'right'
                      }}>
                        <h3 style={{ color: '#28a745' }}>
                          Total: ₹{calculateTotal()}
                        </h3>
                      </div>
                    </div>
                  )}
                </div>

                {/* Modal Footer */}
                <div style={{
                  padding: '20px',
                  borderTop: '1px solid #eee',
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '12px'
                }}>
                  <button
                    onClick={closeModal}
                    style={{
                      padding: '10px 20px',
                      border: '1px solid #ddd',
                      backgroundColor: 'red',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmOrder}
                    disabled={selectedItems.length === 0 || orderLoading}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: selectedItems.length === 0 ? '#ccc' : '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: selectedItems.length === 0 ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {orderLoading ? 'Placing Order...' : 'Place Order'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BrandingDesigns;